if (!customElements.get('l-site-header')) {
	class LTASiteHeader extends HTMLElement {
		constructor() {
			super()
			this.handleClick = this.handleClick.bind(this)
			this.updateHeaderHeight = this.updateHeaderHeight.bind(this)
			this.handleScroll = this.handleScroll.bind(this)
			this.measureScroll = this.measureScroll.bind(this)
		}

		// ------------------------------------------------------------
		// Lifecycle
		// ------------------------------------------------------------

		connectedCallback() {
			this.search = this.querySelector('l-header-search')
			this.drawer = this.querySelector('l-header-drawer')

			this.addEventListener('click', this.handleClick)

			this.resizeObserver = new ResizeObserver(this.updateHeaderHeight)
			this.resizeObserver.observe(this)
			this.updateHeaderHeight()

			this.scrollTicking = false
			window.addEventListener('scroll', this.handleScroll, { passive: true })
			this.measureScroll()
		}

		disconnectedCallback() {
			this.removeEventListener('click', this.handleClick)
			this.resizeObserver?.disconnect()
			window.removeEventListener('scroll', this.handleScroll)
			if (this.scrollFrame) window.cancelAnimationFrame(this.scrollFrame)
		}

		// ------------------------------------------------------------
		// Clicks
		// ------------------------------------------------------------

		handleClick(event) {
			const searchToggle = event.target.closest('[data-search-toggle]')
			if (searchToggle && this.search) {
				event.preventDefault()
				this.search.toggle(searchToggle)

				return
			}

			const panelTrigger = event.target.closest('[data-drawer-panel-trigger]')
			if (panelTrigger && this.drawer) {
				this.search?.close(false)
				this.drawer.openPanel(panelTrigger)
			}
		}

		// ------------------------------------------------------------
		// Sticky
		// ------------------------------------------------------------

		handleScroll() {
			if (this.scrollTicking) return
			this.scrollTicking = true
			this.scrollFrame = window.requestAnimationFrame(this.measureScroll)
		}

		measureScroll() {
			this.scrollTicking = false

			const y = Math.max(0, window.scrollY)

			if (y <= (this.fullHeight ?? this.offsetHeight)) {
				if (!this.classList.contains('is-stuck')) return

				this.classList.remove('is-stuck', 'is-revealed', 'is-animated')
				this.updateHeaderHeight()

				return
			}

			if (this.classList.contains('is-stuck')) return

			this.classList.add('is-stuck')
			window.requestAnimationFrame(() => this.classList.add('is-animated', 'is-revealed'))
		}

		updateHeaderHeight() {
			const root = document.documentElement

			root.style.setProperty('--l-header-height', `${this.offsetHeight}px`)

			if (this.classList.contains('is-stuck')) return

			this.fullHeight = this.offsetHeight
			root.style.setProperty('--l-header-full', `${this.fullHeight}px`)
		}
	}

	customElements.define('l-site-header', LTASiteHeader)
}

if (!customElements.get('l-header-search')) {
	const SEARCH_RESOURCES = 'product,query,collection,article'
	const SEARCH_LIMIT = 6

	class LTAHeaderSearch extends HTMLElement {
		constructor() {
			super()
			this.handleInput = l_debounce(this.search.bind(this), 300)
			this.handleSubmit = this.handleSubmit.bind(this)
			this.handleKeydown = this.handleKeydown.bind(this)
			this.handleFocusOut = this.handleFocusOut.bind(this)
			this.handleOutsideClick = this.handleOutsideClick.bind(this)
			this.handleCloseClick = () => this.close()
			this.handleExternalClose = () => this.close(false)
		}

		// ------------------------------------------------------------
		// Lifecycle
		// ------------------------------------------------------------

		connectedCallback() {
			this.header = this.closest('l-site-header')
			this.form = this.querySelector('[data-search-form]')
			this.input = this.querySelector('[data-search-input]')
			this.closeButton = this.querySelector('[data-search-close]')
			this.results = this.querySelector('[data-search-results]')
			this.status = this.querySelector('[data-search-status]')
			this.sectionId = this.dataset.section
			this.cache = new Map()
			this.desktopQuery = window.matchMedia('(min-width: 1024px)')

			this.input?.addEventListener('input', this.handleInput)
			this.form?.addEventListener('submit', this.handleSubmit)
			this.closeButton?.addEventListener('click', this.handleCloseClick)
			this.addEventListener('keydown', this.handleKeydown)
			this.addEventListener('focusout', this.handleFocusOut)
			document.addEventListener('l:drawer:open', this.handleExternalClose)
			this.desktopQuery.addEventListener('change', this.handleExternalClose)
		}

		disconnectedCallback() {
			this.input?.removeEventListener('input', this.handleInput)
			this.form?.removeEventListener('submit', this.handleSubmit)
			this.closeButton?.removeEventListener('click', this.handleCloseClick)
			this.removeEventListener('keydown', this.handleKeydown)
			this.removeEventListener('focusout', this.handleFocusOut)
			document.removeEventListener('l:drawer:open', this.handleExternalClose)
			document.removeEventListener('click', this.handleOutsideClick)
			this.desktopQuery?.removeEventListener('change', this.handleExternalClose)
			this.controller?.abort()
		}

		// ------------------------------------------------------------
		// Open / close
		// ------------------------------------------------------------

		get isOpen() {
			return this.classList.contains('is-open')
		}

		toggle(trigger) {
			this.isOpen ? this.close() : this.open(trigger)
		}

		open(trigger) {
			if (this.isOpen) return

			this.trigger = trigger
			this.classList.add('is-open')
			this.header?.classList.add('is-search-open')
			this.syncToggles(true)
			document.addEventListener('click', this.handleOutsideClick)

			this.input?.focus({ preventScroll: true })
		}

		close(restoreFocus = true) {
			if (!this.isOpen) return

			this.classList.remove('is-open')
			this.header?.classList.remove('is-search-open')
			this.syncToggles(false)
			document.removeEventListener('click', this.handleOutsideClick)

			if (restoreFocus) {
				this.trigger?.focus({ preventScroll: true })
			} else if (this.contains(document.activeElement)) {
				document.activeElement.blur()
			}
			this.trigger = null
		}

		syncToggles(isOpen) {
			document.querySelectorAll(`[aria-controls="${CSS.escape(this.id)}"]`).forEach((el) => {
				el.setAttribute('aria-expanded', isOpen ? 'true' : 'false')
			})
		}

		handleKeydown(event) {
			if (event.key !== 'Escape') return

			event.preventDefault()
			event.stopPropagation()
			this.close()
		}

		handleFocusOut(event) {
			const next = event.relatedTarget
			if (!this.isOpen || !next || this.contains(next) || next.closest?.('[data-search-toggle]')) return

			this.close(false)
		}

		handleOutsideClick(event) {
			if (this.contains(event.target) || event.target.closest?.('[data-search-toggle]')) return

			this.close(false)
		}

		handleSubmit(event) {
			if (this.input?.value.trim()) return

			event.preventDefault()
			this.input?.focus()
		}

		// ------------------------------------------------------------
		// Predictive search
		// ------------------------------------------------------------

		search() {
			const term = this.input?.value.trim() ?? ''

			if (!term) {
				this.controller?.abort()
				this.render('')

				return
			}

			const key = term.toLowerCase()
			if (this.cache.has(key)) {
				this.controller?.abort()
				this.render(this.cache.get(key))

				return
			}

			this.fetchResults(term, key)
		}

		fetchResults(term, key) {
			this.controller?.abort()
			const controller = new AbortController()
			this.controller = controller

			const params = new URLSearchParams({
				q: term,
				section_id: this.sectionId,
				'resources[type]': SEARCH_RESOURCES,
				'resources[limit]': SEARCH_LIMIT,
				'resources[limit_scope]': 'each',
				'resources[options][prefix]': 'last',
			})

			this.setLoading(true)

			fetch(`${window.LTATheme.routes.predictive_search_url}?${params}`, { signal: controller.signal })
				.then((response) => {
					if (!response.ok) throw new Error(`Predictive search failed: ${response.status}`)

					return response.text()
				})
				.then((html) => {
					const markup = l_getSectionDOM(html, `#shopify-section-${this.sectionId}`)?.innerHTML ?? ''
					this.cache.set(key, markup)
					this.render(markup)
				})
				.catch((error) => {
					if (error.name === 'AbortError') return

					console.error(error)
					this.setLoading(false)
				})
		}

		render(markup) {
			this.setLoading(false)
			this.results.innerHTML = markup
			this.results.hidden = !markup

			const statusText = this.results.querySelector('[data-search-status-text]')?.dataset.searchStatusText
			if (this.status) this.status.textContent = statusText ?? ''
		}

		setLoading(isLoading) {
			this.classList.toggle('is-loading', isLoading)
			this.results?.setAttribute('aria-busy', isLoading ? 'true' : 'false')
		}
	}

	customElements.define('l-header-search', LTAHeaderSearch)
}

if (!customElements.get('l-header-drawer')) {
	const DRAWER_DURATION = 400

	class LTAHeaderDrawer extends LTADrawer {
		connectedCallback() {
			super.connectedCallback()

			this.header = this.closest('l-site-header')
			this.backButton = this.querySelector('[data-drawer-back]')
			this.desktopQuery = window.matchMedia('(min-width: 1024px)')
			this._onBreakpointChange = () => this.classList.contains('is-active') && this.drawer_close()
			this._onPanelClick = this._onPanelClick.bind(this)

			this.querySelectorAll('[data-drawer-panel][hidden]').forEach((panel) => {
				panel.hidden = false
			})

			this.addEventListener('click', this._onPanelClick)
			this.desktopQuery.addEventListener('change', this._onBreakpointChange)
		}

		disconnectedCallback() {
			this.removeEventListener('click', this._onPanelClick)
			this.desktopQuery?.removeEventListener('change', this._onBreakpointChange)
			window.clearTimeout(this.closingTimer)
		}

		// ------------------------------------------------------------
		// Open / close
		// ------------------------------------------------------------

		openPanel(trigger) {
			this.drawer_open(trigger, trigger)
		}

		drawer_open(triggerEl, panelTrigger = null) {
			window.clearTimeout(this.closingTimer)
			this.header?.classList.remove('is-menu-closing')
			this.header?.classList.add('is-menu-open')
			this._closeSubPanel(false)
			this._setDirect(Boolean(panelTrigger))
			if (panelTrigger) this._openSubPanel(panelTrigger)

			super.drawer_open(triggerEl)
		}

		drawer_close() {
			const wasOpen = this.classList.contains('is-active')

			super.drawer_close()

			if (!wasOpen) return

			if (this.isDirect) this.openSubTrigger?.setAttribute('aria-expanded', 'false')

			this.header?.classList.remove('is-menu-open')
			this.header?.classList.add('is-menu-closing')
			this.closingTimer = window.setTimeout(() => {
				this.header?.classList.remove('is-menu-closing')
				this._closeSubPanel(false)
				this._setDirect(false)
			}, DRAWER_DURATION)
		}

		_setDirect(isDirect) {
			this.isDirect = isDirect
			this.classList.toggle('is-direct', isDirect)
		}

		drawer_closeOutsideOnEscape(event) {
			if (event.code?.toUpperCase() === 'ESCAPE' && this.openSubPanel && !this.isDirect) {
				this._closeSubPanel()

				return
			}

			super.drawer_closeOutsideOnEscape(event)
		}

		// ------------------------------------------------------------
		// Sub-panels
		// ------------------------------------------------------------

		_onPanelClick(event) {
			const subTrigger = event.target.closest('[data-drawer-open]')
			if (subTrigger) {
				this._openSubPanel(subTrigger)

				return
			}

			if (event.target.closest('[data-drawer-back]')) {
				this._closeSubPanel()

				return
			}

			if (event.target.closest('[data-drawer-link]')) this.drawer_close()
		}

		_openSubPanel(trigger) {
			const panel = document.getElementById(trigger.getAttribute('aria-controls'))
			const nav = panel?.closest('[data-drawer-nav]')
			if (!panel || !nav) return

			panel.classList.add('is-active')
			nav.classList.add('is-sub-open')
			trigger.setAttribute('aria-expanded', 'true')

			this.openSubTrigger = trigger
			this.openSubPanel = panel

			if (this.backButton && !this.isDirect) {
				this.backButton.hidden = false
				this.backButton.focus({ preventScroll: true })
			}
		}

		_closeSubPanel(restoreFocus = true) {
			const panel = this.openSubPanel
			if (!panel) return

			const trigger = this.openSubTrigger

			panel.classList.remove('is-active')
			panel.closest('[data-drawer-nav]')?.classList.remove('is-sub-open')
			trigger?.setAttribute('aria-expanded', 'false')

			this.openSubPanel = null
			this.openSubTrigger = null

			if (this.backButton) this.backButton.hidden = true

			if (restoreFocus) trigger?.focus({ preventScroll: true })
		}
	}

	customElements.define('l-header-drawer', LTAHeaderDrawer)
}

